// src/Components/notifications/NotificationDetailPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { notificationAPI } from '../../Components/services/api';
import AdminLayout from '../layout/AdminLayout';

const NotificationDetailContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markingRead, setMarkingRead] = useState(false);

  const fetchNotification = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('📡 Fetching notification with ID:', id);
      
      const response = await notificationAPI.getById(id);
      console.log('📥 Notification API response:', response.data);
      
      if (response.data && response.data.success) {
        const data = response.data.data;
        setNotification(data);
        console.log('✅ Notification loaded:', data);
      } else {
        setError('Notification not found');
        toast.error('Notification not found');
        setTimeout(() => navigate('/notifications'), 2000);
      }
    } catch (error) {
      console.error('❌ Error fetching notification:', error);
      setError(error.response?.data?.message || 'Failed to load notification');
      toast.error('Failed to load notification');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    console.log('🔔 NotificationDetail mounted with id:', id);
    if (id) {
      fetchNotification();
    } else {
      setError('No notification ID provided');
      setLoading(false);
    }
  }, [id, fetchNotification]);

  const handleMarkAsRead = async () => {
    if (!id) {
      toast.error('Invalid notification ID');
      return;
    }

    setMarkingRead(true);
    try {
      console.log('📖 Marking notification as read. ID:', id);
      
      const response = await notificationAPI.markAsRead(id);
      console.log('📥 Mark as read response:', response.data);
      
      if (response.data && response.data.success) {
        setNotification(prev => ({ ...prev, isRead: true }));
        toast.success('Marked as read');
        window.dispatchEvent(new Event('notification-read'));
      } else {
        toast.error(response.data?.message || 'Failed to mark as read');
      }
    } catch (error) {
      console.error('❌ Error marking as read:', error);
      
      if (error.response) {
        toast.error(error.response.data?.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        toast.error('No response from server. Please check your connection.');
      } else {
        toast.error(error.message || 'Failed to mark as read');
      }
    } finally {
      setMarkingRead(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    try {
      await notificationAPI.delete(id);
      toast.success('Notification deleted');
      window.dispatchEvent(new Event('notification-read'));
      navigate('/notifications');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'SUBSCRIPTION':
        return '📦';
      case 'USER':
        return '👤';
      case 'PAYMENT':
        return '💰';
      case 'SYSTEM':
        return '⚙️';
      default:
        return '🔔';
    }
  };

  const getNotificationTypeLabel = (type) => {
    switch (type) {
      case 'SUBSCRIPTION':
        return 'Subscription';
      case 'USER':
        return 'User';
      case 'PAYMENT':
        return 'Payment';
      case 'SYSTEM':
        return 'System';
      default:
        return 'General';
    }
  };

  const getActionLabel = (actionType) => {
    switch (actionType) {
      case 'CREATED':
        return 'Created';
      case 'UPDATED':
        return 'Updated';
      case 'DELETED':
        return 'Deleted';
      case 'INVITED':
        return 'User Invited';
      case 'DEACTIVATED':
        return 'User Deactivated';
      case 'REACTIVATED':
        return 'User Reactivated';
      case 'ROLE_CHANGED':
        return 'Role Changed';
      case 'EXTENDED':
        return 'Subscription Extended';
      case 'CANCELLED':
        return 'Subscription Cancelled';
      case 'RESEND_INVITATION':
        return 'Resend Invitation';
      default:
        return actionType || 'Action';
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'N/A';
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading notification...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-6xl mb-4">🔍</p>
        <p className="text-lg text-gray-600">{error}</p>
        <button
          onClick={() => navigate('/notifications')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to notifications
        </button>
      </div>
    );
  }

  if (!notification) {
    return (
      <div className="text-center py-12">
        <p className="text-6xl mb-4">📭</p>
        <p className="text-lg text-gray-600">No notification found</p>
        <button
          onClick={() => navigate('/notifications')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to notifications
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Back button */}
      <button
        onClick={() => navigate('/notifications')}
        className="flex items-center text-gray-600 hover:text-gray-800 mb-6"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Notifications
      </button>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 text-4xl">
              {getNotificationIcon(notification.type)}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{notification.title}</h1>
              <div className="flex items-center space-x-3 mt-2">
                <span className="text-sm px-3 py-1 bg-gray-100 text-gray-600 rounded-full">
                  {getNotificationTypeLabel(notification.type)}
                </span>
                {notification.actionType && (
                  <span className="text-sm px-3 py-1 bg-blue-100 text-blue-600 rounded-full">
                    {getActionLabel(notification.actionType)}
                  </span>
                )}
                <span className={`text-sm px-3 py-1 rounded-full ${
                  notification.isRead ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-600'
                }`}>
                  {notification.isRead ? 'Read' : 'Unread'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <p className="text-gray-700 whitespace-pre-line">{notification.message}</p>

          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            {notification.entityType && (
              <div>
                <span className="font-medium text-gray-500">Entity Type:</span>
                <span className="ml-2 text-gray-700">{notification.entityType}</span>
              </div>
            )}
            {notification.entityId && (
              <div>
                <span className="font-medium text-gray-500">Entity ID:</span>
                <span className="ml-2 text-gray-700 font-mono">{notification.entityId}</span>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-500">Received:</span>
              <span className="ml-2 text-gray-700">
                {new Date(notification.createdAt).toLocaleString()}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-500">Time ago:</span>
              <span className="ml-2 text-gray-700">
                {formatTimeAgo(notification.createdAt)}
              </span>
            </div>
          </div>

          <div className="mt-6 flex space-x-3">
            {!notification.isRead && (
              <button
                onClick={handleMarkAsRead}
                disabled={markingRead}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {markingRead ? 'Marking...' : 'Mark as Read'}
              </button>
            )}
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              Delete Notification
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationDetailPage = () => {
  return (
    <AdminLayout activeTab="notifications">
      <NotificationDetailContent />
    </AdminLayout>
  );
};

export default NotificationDetailPage;